/**
 * Plan location <-> URL, safe in-app return targets, and history-aware
 * completion of child flows (Swap, Browse, Step-by-Step, Review, ...).
 *
 * PLAN URL — the single source of truth for where the user is on Plan:
 *   /plan?view=daily&date=YYYY-MM-DD    that exact day
 *   /plan?view=weekly&date=YYYY-MM-DD   the Sun-Sat week containing `date`
 * `date` is an anchor: switching Daily <-> Weekly keeps the same day / week.
 * Legacy links keep working: a bare `?date=` is Daily, `?week=<n>` is Weekly n
 * weeks from the current week, and a bare `/plan` is Weekly for today.
 *
 * RETURN TARGETS — a child flow receives its origin as an encoded `return`
 * param and only ever navigates to a same-app path (see safeReturnPath).
 *
 * FLOW HISTORY — a screen pushed into a child flow carries a depth marker in
 * router state (startFlowState / continueFlowState). Finishing the flow pops
 * straight back to the origin entry, so Back never re-opens a completed flow
 * (e.g. a Swap whose outgoing row no longer exists). When the origin entry is
 * not known to be in history (deep link, new tab) it replaces instead.
 */
import { formatDateString, getTodayString, getWeekStart, normalizeDate } from './dateUtils';

export var PLAN_PATH = '/plan';

var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
var WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Router-state keys (kept short; they ride along in history.state).
var DEPTH_KEY = 'fxFlowDepth'; // entries above the flow's origin (0/absent = unknown)
var BACK_KEY = 'fxFlowBack';   // the previous entry is this screen's parent

/** True for a real calendar date in YYYY-MM-DD form (rejects 2026-02-31). */
export function isDateString(value) {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false;
  return formatDateString(normalizeDate(value)) === value;
}

/** YYYY-MM-DD shifted by a whole number of days (DST-safe, local time). */
export function addDays(dateStr, days) {
  var d = normalizeDate(dateStr);
  d.setDate(d.getDate() + days);
  return formatDateString(d);
}

/** Whole weeks from today's Sun-Sat week to the week containing `dateStr`. */
export function weekOffsetFor(dateStr, today) {
  var from = getWeekStart(today || getTodayString()).getTime();
  var to = getWeekStart(dateStr).getTime();
  return Math.round((to - from) / WEEK_MS);
}

/**
 * Read the Plan location from URL search params.
 * Returns { view: 'daily'|'weekly', date: 'YYYY-MM-DD', isLegacy: bool } where
 * isLegacy means the URL carries params that should be rewritten to the
 * canonical form (a bare /plan is NOT legacy — it simply follows today).
 */
export function readPlanLocation(searchParams, today) {
  today = today || getTodayString();
  var rawView = searchParams.get('view');
  var rawDate = searchParams.get('date');
  var rawWeek = searchParams.get('week');

  var view = (rawView === 'daily' || rawView === 'weekly') ? rawView : null;
  var date = isDateString(rawDate) ? rawDate : null;

  if (!date && rawWeek != null && rawWeek !== '') {
    var n = parseInt(rawWeek, 10);
    if (!Number.isNaN(n)) date = addDays(today, 7 * n);
  }
  if (!view) view = isDateString(rawDate) ? 'daily' : 'weekly';

  var isLegacy = rawWeek != null
    || (rawView != null && rawView !== view)
    || (rawDate != null && rawDate !== date)
    || (rawDate != null && rawView == null);

  return { view: view, date: date || today, isLegacy: isLegacy };
}

/** Canonical search params for a Plan location. */
export function planSearchParams(loc) {
  var sp = new URLSearchParams();
  sp.set('view', loc.view === 'daily' ? 'daily' : 'weekly');
  sp.set('date', loc.date);
  return sp;
}

/** Canonical Plan URL for a location, e.g. /plan?view=daily&date=2026-10-05. */
export function planPath(loc) {
  return PLAN_PATH + '?' + planSearchParams(loc).toString();
}

/**
 * Plan URL that shows a set of dates the user just planned: Daily for one day,
 * otherwise Weekly anchored on the earliest date.
 */
export function planPathForDates(dates) {
  var list = (dates || []).filter(isDateString).slice().sort();
  if (list.length === 0) return PLAN_PATH;
  return planPath({ view: list.length === 1 ? 'daily' : 'weekly', date: list[0] });
}

/**
 * Accept only a same-app absolute path ("/x?..."). Absolute URLs,
 * protocol-relative "//host", backslashes and control characters fall back.
 */
export function safeReturnPath(value, fallback) {
  if (typeof value !== 'string') return fallback;
  if (value.charAt(0) !== '/' || value.charAt(1) === '/') return fallback;
  if (/[\s\u0000-\u001F\u007F\\]/.test(value)) return fallback;
  return value;
}

/** The current in-app path (pathname + search) of a router location. */
export function currentPath(location) {
  if (!location) return '';
  return (location.pathname || '') + (location.search || '');
}

/** `path?k=v&...` with every value encoded; null/undefined/'' values are omitted. */
export function buildUrl(path, params) {
  var sp = new URLSearchParams();
  Object.keys(params || {}).forEach(function (key) {
    var value = params[key];
    if (value === undefined || value === null || value === '') return;
    sp.set(key, String(value));
  });
  var qs = sp.toString();
  return qs ? path + '?' + qs : path;
}

function flowDepth(location) {
  var state = location && location.state;
  var d = state && state[DEPTH_KEY];
  return (typeof d === 'number' && d > 0) ? d : 0;
}

/** Router state for the FIRST screen of a child flow, pushed from its origin. */
export function startFlowState(extra) {
  var state = Object.assign({}, extra || {});
  state[DEPTH_KEY] = 1;
  state[BACK_KEY] = true;
  return state;
}

/** Router state for a deeper screen of the SAME flow (e.g. a Swap candidate's detail). */
export function continueFlowState(location, extra) {
  var depth = flowDepth(location);
  var state = Object.assign({}, extra || {});
  state[DEPTH_KEY] = depth > 0 ? depth + 1 : 0;
  state[BACK_KEY] = true;
  return state;
}

/**
 * Leave a finished flow for its origin without leaving the flow behind Back:
 * pop straight to the origin entry when it is known to be in history,
 * otherwise replace the current entry with `fallbackPath`.
 */
export function finishFlow(navigate, location, fallbackPath) {
  var depth = flowDepth(location);
  if (depth > 0) {
    navigate(-depth);
    return;
  }
  navigate(fallbackPath, { replace: true });
}

/**
 * Return to the screen that opened this one (e.g. a candidate detail back to
 * its browser). Pops when that parent is known to be the previous entry,
 * otherwise replaces the current entry with `fallbackPath`.
 */
export function returnToParent(navigate, location, fallbackPath) {
  var state = location && location.state;
  if (state && state[BACK_KEY]) {
    navigate(-1);
    return;
  }
  navigate(fallbackPath, { replace: true });
}
